import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import FareFamilyFlightTileV2TimeLine from '../V2/FareFamily/FareFamilyFlightTileV2TimeLine'
import Image from 'components/Common/Image'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { INTL_LONG_WEEKDAY, INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useIntl } from 'react-intl'
import { useMemo } from 'react'
import { getTotalStopCount } from 'lib/flights/flightUtils'

const AirlineLogoContainer = styled(Group)`
  flex-shrink: 0;
  width: ${rem(40)};
  height: ${rem(40)};

  ${mediaQueryUp.tablet} {
    &:not(.desktopShowLogo) {
      display: none;
    }
  }
`

const AirlineLogoImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`

const SegmentDayLabel = styled(BodyText)`
  ${mediaQueryUp.tablet} {
    &.desktopShowLogo {
      margin-inline-start: ${rem(56)};
    }
  }
`

interface Props {
  className?: string
  journey: App.JourneyV2
  desktopShowLogo: boolean
  showExactDate: boolean
  /** When true, show fixed “Depart” / “Return” labels instead of weekday or exact dates (no hotel dates yet). */
  useUndatedSegmentLabels?: boolean
  segment: 'departing' | 'returning'
  route: App.CharterBundleRoute
}

function CharterTileIndividualFlight({
  className,
  journey,
  desktopShowLogo,
  showExactDate,
  useUndatedSegmentLabels = false,
  segment,
  route,
}: Props) {
  const intl = useIntl()
  const flights = journey.flightGroup.flights
  const totalStops = getTotalStopCount(flights)
  const duration = journey.flightGroup.totalFlightDuration
  const dayName = useMemo(() => {
    if (useUndatedSegmentLabels) {
      return segment === 'departing'
        ? intl.formatMessage({
            id: 'Qq5zrcIm9w1UqBpi3AdQ',
            defaultMessage: 'Depart',
            description: 'Short label on the Maldives charter return tile for the outbound leg when the guest has not chosen stay dates yet; appears above sample times in place of a calendar date.',
          })
        : intl.formatMessage({
            id: 'hY_so0XTasixNTu2NQnC',
            defaultMessage: 'Return',
            description: 'Short label on the Maldives charter return tile for the inbound leg when the guest has not chosen stay dates yet; appears above sample times in place of a calendar date.',
          })
    }
    return intlDateFormatter(
      flights[0].departingDate,
      showExactDate ? INTL_SHORT_DAY_NAME_DAY_MONTH : INTL_LONG_WEEKDAY,
      intl.locale,
    )
  }, [flights, intl, segment, showExactDate, useUndatedSegmentLabels])

  const routeInfo = CHARTER_FLIGHT_DETAILS[route]

  return <Group direction="vertical" gap={8} className={className}>
    <SegmentDayLabel variant="small" className={clsx({ desktopShowLogo })}>
      {segment === 'departing' && dayName}
      {segment === 'returning' && dayName}
    </SegmentDayLabel>
    <Group direction="horizontal" gap={16}>
      <AirlineLogoContainer direction="horizontal" verticalAlign="center" horizontalAlign="center" className={clsx({ desktopShowLogo })}>
        <AirlineLogoImage
          width={40}
          image={routeInfo.operatorLogo}
        />
      </AirlineLogoContainer>
      <FareFamilyFlightTileV2TimeLine
        departureAirport={flights[0].departureAirport}
        departureTime={flights[0].departingTime}
        departureDate={flights[0].departingDate}
        arrivalAiport={flights.at(-1)!.arrivalAirport}
        arrivalTime={flights.at(-1)!.arrivalTime}
        arrivalDate={flights.at(-1)!.arrivalDate}
        stops={totalStops}
        duration={intl.formatMessage(
          {
            id: 'xASEaYaWtLQLwu4e1RIe',
            defaultMessage: '{hours}h {minutes}m',
            description: 'Flight duration in hours and minutes in the flight search or booking flow',
          },
          { hours: duration.hours, minutes: duration.minutes },
        )}
        showDate={false}
      />
    </Group>
  </Group>
}

export default CharterTileIndividualFlight
